import { useEffect, useRef, useState } from "react";
import { resolveMediaUrl } from "../../lib/media-url";

export type RecordingPlayerProps = {
  src: string;
  /** Visible label when paused — defaults to "Hoor je opname". */
  pausedLabel?: string;
  /** Visible label when playing — defaults to "Pauzeer". */
  playingLabel?: string;
  ariaPaused?: string;
  ariaPlaying?: string;
};

/**
 * Single canonical playback pill for a stored audio file. Used by the
 * post-exam rapport and history surfaces. Replaces native `<audio
 * controls>` so the affordance reads the same everywhere.
 *
 * Single-play semantics: pauses every other `<audio>` on the page
 * before starting so two rows can't overlap.
 */
export function RecordingPlayer({
  src,
  pausedLabel = "Hoor je opname",
  playingLabel = "Pauzeer",
  ariaPaused,
  ariaPlaying,
}: RecordingPlayerProps) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    setPlaying(false);
  }, [src]);

  function pauseOtherAudio() {
    if (typeof document === "undefined") return;
    document.querySelectorAll("audio").forEach((a) => {
      if (a !== ref.current && !a.paused) a.pause();
    });
  }

  function toggle() {
    const a = ref.current;
    if (!a) return;
    if (a.paused) {
      pauseOtherAudio();
      a.play()
        .then(() => setPlaying(true))
        .catch(() => {});
    } else {
      a.pause();
      setPlaying(false);
    }
  }

  const ariaLabel = playing
    ? (ariaPlaying ?? `Pauzeer ${pausedLabel.toLowerCase()}`)
    : (ariaPaused ?? `Speel ${pausedLabel.toLowerCase()} af`);

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        aria-label={ariaLabel}
        className="inline-flex items-center gap-2 rounded-md border border-[var(--border)] bg-[var(--panel-soft)] hover:bg-[var(--panel)] hover:border-[var(--accent)]/40 px-3 py-1.5 text-xs text-[var(--ink-muted)] hover:text-[var(--ink)] transition-colors"
      >
        <span aria-hidden>{playing ? "❙❙" : "▶"}</span>
        <span>{playing ? playingLabel : pausedLabel}</span>
      </button>
      <audio
        ref={ref}
        src={resolveMediaUrl(src)}
        preload="metadata"
        playsInline
        className="sr-only"
        aria-hidden
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />
    </>
  );
}
